import { useRef, useState, useEffect, createElement } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

export function useModal(component, baseProps) {
    const isMounted = useRef(true);
    const [modal, setModal] = useState();

    useEffect(() => {
        isMounted.current = true;
        return () => (isMounted.current = false);
    }, [modal]);

    const closeModal = (onClose) => () => {
        if (!isMounted.current) {
            return;
        }

        setModal(null);

        if (onClose && typeof onClose === "function") {
            onClose();
        }
    };

    const openModal = (instanceProps, onClose) => {
        if (!isMounted.current) {
            return;
        }

        const modalElement = createElement(component, { ...baseProps, ...instanceProps, close: closeModal(onClose) });
        const modalWrapper = createElement("div", {}, modalElement);
        const modalPortal = createPortal(modalWrapper, document.getElementById("modal"));
        setModal(modalPortal);
    };

    return [modal, openModal];
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = (err) => reject(err);
        document.body.appendChild(script);
    });
}

export function useLoadScript({ src, onLoad, onError }) {
    useEffect(() => {
        loadScript(src)
            .then(onLoad)
            .catch(onError);

        return () => {
            const scriptElement = document.querySelector(`script[src="${src}"]`)
            if (scriptElement) document.body.removeChild(scriptElement)
        }
    }, [src, onLoad, onError]);
}

export function useOnline() {
    const isOnline = useSelector(state => state.app.online);
    return isOnline;
}

export function useRefData() {
    const refData = useSelector(state => state.app.refData);
    return refData;
}

export function useAuthContext() {
    const authContext = useSelector(state => {
        const result = {
            isAuthenticated: state.auth.isAuthenticated,
            isActivated: state.auth.isActivated,
            isSystemAdmin: state.auth.isSystemAdmin,
            creditLimit: state.auth.creditLimit,
            isAdmin: state.auth.isAdmin,
        };
        if (result.isAuthenticated) {
            result.accountId = state.auth.accountId;
            result.accountName = state.auth.accountName;
            result.accountType = state.auth.accountType;
            result.userId = state.auth.userId;
            result.fullName = state.auth.fullName;
            result.email = state.auth.email;
            result.canChangePassword = state.auth.canChangePassword;
            result.canBePublishedTo = state.auth.canBePublishedTo;
            result.canPublishToMPIWholeFarmDataProject = canPublishToMPIWholeFarmDataProject(state.auth);
            result.canPublishWithoutSubscription = canPublishWithoutSubscription(state.auth);
            result.orgApiAccess = state.auth.orgApiAccess;
            result.isStudent = state.auth.accountType === "Student";
            result.parentAccountId = state.auth.parentAccountId;
        }
        return result;
    });
    return authContext;
}

export function useNavigate() {
    const history = useHistory();
    return (path, state) => {
        history.push(path, state);
    }
}

export function useIsPaidVersionOfOverseer() {
    const { deploymentType } = useRefData();
    return !deploymentType || deploymentType === "Commercial";
}

export function useIsOverseerEd() {
    const { deploymentType } = useRefData();
    return deploymentType === "Education";
}

export function useIsOverseerSci() {
    const { deploymentType } = useRefData();
    return deploymentType === "Science";
}

export function useReferrer() {
    const location = useLocation();
    return location.state?.referrer || "/";
}

export const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const debounce = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(debounce);
    }, [value, delay]);

    return debouncedValue;
};

function canPublishToMPIWholeFarmDataProject(authState) {
    return authState.canPublishToMPIWholeFarmDataProject || authState.isSystemAdmin;
}

function canPublishWithoutSubscription(authState) {
    return authState.canPublishToMPIWholeFarmDataProject || authState.isSystemAdmin;
}